/**
 * Tabbed content block: desktop tabs, mobile custom dropdown, prev/next arrows.
 */
const mqDesktop = window.matchMedia("(min-width: 992px)");

function normalizeIndex(block, index) {
  const n = parseInt(block.dataset.tabCount, 10) || 0;
  if (n < 1) {
    return 0;
  }
  const i = ((index % n) + n) % n;
  return i;
}

function getDropdownOptionElements(block) {
  return Array.from(block.querySelectorAll("[data-tabbed-dropdown-option]"));
}

function setDropdownUi(block, index) {
  const trigger = block.querySelector("[data-tabbed-dropdown-trigger]");
  const valueEl = block.querySelector("[data-tabbed-dropdown-value]");
  const options = getDropdownOptionElements(block);
  const i = normalizeIndex(block, index);
  const selected = options[i];

  options.forEach((opt, o) => {
    opt.setAttribute("aria-selected", o === i ? "true" : "false");
  });

  if (valueEl && selected) {
    valueEl.textContent = selected.textContent.trim();
  }

  if (trigger && selected) {
    const prefix =
      block.dataset.tabbedCurrentTabPrefix?.trim() || "Current tab";
    trigger.setAttribute(
      "aria-label",
      `${prefix}: ${selected.textContent.trim()}`,
    );
  }
}

function closeDropdown(block, focusTrigger = true) {
  const trigger = block.querySelector("[data-tabbed-dropdown-trigger]");
  const menu = block.querySelector("[data-tabbed-dropdown-menu]");
  if (!menu || !trigger) {
    return;
  }
  menu.setAttribute("hidden", "");
  trigger.setAttribute("aria-expanded", "false");
  if (focusTrigger) {
    trigger.focus();
  }
}

function openDropdown(block) {
  const trigger = block.querySelector("[data-tabbed-dropdown-trigger]");
  const menu = block.querySelector("[data-tabbed-dropdown-menu]");
  if (!menu || !trigger) {
    return;
  }
  menu.removeAttribute("hidden");
  trigger.setAttribute("aria-expanded", "true");
  const options = getDropdownOptionElements(block);
  const active = options.findIndex(
    (el) => el.getAttribute("aria-selected") === "true",
  );
  const toFocus = options[active >= 0 ? active : 0];
  if (toFocus) {
    toFocus.focus();
  }
}

function toggleDropdown(block) {
  const menu = block.querySelector("[data-tabbed-dropdown-menu]");
  if (!menu) {
    return;
  }
  if (menu.hasAttribute("hidden")) {
    openDropdown(block);
  } else {
    closeDropdown(block, false);
  }
}

function setActiveTab(block, index) {
  const i = normalizeIndex(block, index);
  const tabs = block.querySelectorAll("[data-tabbed-tab]");
  const panels = block.querySelectorAll("[data-tabbed-panel]");

  tabs.forEach((tab, t) => {
    const on = t === i;
    tab.setAttribute("aria-selected", on ? "true" : "false");
    tab.tabIndex = on ? 0 : -1;
  });

  panels.forEach((panel, p) => {
    if (p === i) {
      panel.classList.add("is-active");
    } else {
      panel.classList.remove("is-active");
    }
  });

  setDropdownUi(block, i);
}

function initTabbedBlock(block) {
  const dropdown = block.querySelector("[data-tabbed-dropdown]");
  const trigger = block.querySelector("[data-tabbed-dropdown-trigger]");
  const menu = block.querySelector("[data-tabbed-dropdown-menu]");
  const prevBtn = block.querySelector("[data-tabbed-prev]");
  const nextBtn = block.querySelector("[data-tabbed-next]");
  const tablist = block.querySelector("[data-tabbed-tablist]");
  let active = 0;

  const apply = (idx, { closeMenu = true } = {}) => {
    active = normalizeIndex(block, idx);
    setActiveTab(block, active);
    if (closeMenu && dropdown) {
      closeDropdown(block, false);
    }
  };

  if (trigger && menu && dropdown) {
    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleDropdown(block);
    });

    trigger.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown") {
        if (menu.hasAttribute("hidden")) {
          e.preventDefault();
          openDropdown(block);
        }
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (menu.hasAttribute("hidden")) {
          openDropdown(block);
        } else {
          closeDropdown(block);
        }
        return;
      }
      if (e.key === "Escape" && !menu.hasAttribute("hidden")) {
        e.preventDefault();
        closeDropdown(block);
      }
    });

    menu.addEventListener("click", (e) => {
      const opt = e.target.closest("[data-tabbed-dropdown-option]");
      if (!opt || !menu.contains(opt)) {
        return;
      }
      const idx = parseInt(opt.dataset.tabIndex, 10);
      if (Number.isNaN(idx)) {
        return;
      }
      apply(idx, { closeMenu: true });
      closeDropdown(block);
    });

    menu.addEventListener("keydown", (e) => {
      const options = getDropdownOptionElements(block);
      const current = options.findIndex((el) => document.activeElement === el);
      if (e.key === "Escape") {
        e.preventDefault();
        closeDropdown(block);
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        let next = current;
        if (next < 0) {
          next = 0;
        }
        if (e.key === "ArrowDown") {
          next = Math.min(next + 1, options.length - 1);
        } else {
          next = Math.max(next - 1, 0);
        }
        options[next]?.focus();
      }
      if (e.key === "Enter" || e.key === " ") {
        const focused = options.find((el) => document.activeElement === el);
        if (focused) {
          e.preventDefault();
          const idx = parseInt(focused.dataset.tabIndex, 10);
          if (!Number.isNaN(idx)) {
            apply(idx, { closeMenu: true });
            closeDropdown(block);
          }
        }
      }
    });

    document.addEventListener("click", (e) => {
      if (!dropdown.contains(e.target)) {
        closeDropdown(block, false);
      }
    });
  }

  if (prevBtn) {
    prevBtn.addEventListener("click", () =>
      apply(active - 1, { closeMenu: true }),
    );
  }

  if (nextBtn) {
    nextBtn.addEventListener("click", () =>
      apply(active + 1, { closeMenu: true }),
    );
  }

  if (tablist) {
    tablist.addEventListener("click", (e) => {
      const t = e.target.closest("[data-tabbed-tab]");
      if (!t || !tablist.contains(t)) {
        return;
      }
      const idx = parseInt(t.dataset.tabIndex, 10);
      if (Number.isNaN(idx)) {
        return;
      }
      apply(idx, { closeMenu: true });
    });

    tablist.addEventListener("keydown", (e) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
        return;
      }
      const tabs = Array.from(tablist.querySelectorAll("[data-tabbed-tab]"));
      const current = tabs.findIndex(
        (el) => el.getAttribute("aria-selected") === "true",
      );
      if (current < 0) {
        return;
      }
      e.preventDefault();
      let next = current;
      if (e.key === "ArrowRight") {
        next = normalizeIndex(block, current + 1);
      }
      if (e.key === "ArrowLeft") {
        next = normalizeIndex(block, current - 1);
      }
      if (e.key === "Home") {
        next = 0;
      }
      if (e.key === "End") {
        next = tabs.length - 1;
      }
      apply(next, { closeMenu: true });
      const btn = tabs[next];
      if (btn) {
        btn.focus();
      }
    });
  }

  apply(0, { closeMenu: true });

  const syncMq = () => {
    setActiveTab(block, active);
    closeDropdown(block, false);
  };

  if (typeof mqDesktop.addEventListener === "function") {
    mqDesktop.addEventListener("change", syncMq);
  } else {
    mqDesktop.addListener(syncMq);
  }
}

function initTabbedContent(root = document) {
  root.querySelectorAll("[data-tabbed-content]").forEach(initTabbedBlock);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initTabbedContent());
} else {
  initTabbedContent();
}
